import { Box, Tooltip } from "@mui/material";
import { ExpandMore, OpenInFullOutlined as AlternativesIcon } from "@mui/icons-material";
import { useState } from "react";
import FoodRowCore, { rowIconBtn } from "./FoodRowCore";
import SubstitutionsPanel from "./SubstitutionsPanel";
import { v2 } from "../ui/v2Tokens";
import {
  promoteAlternative,
  withMatchedAlternatives,
  withServing,
  type FoodItemV2,
  type SubGroupV2,
} from "../model/nutritionV2Model";

type Props = {
  item: FoodItemV2;
  onChange: (item: FoodItemV2) => void;
  onDelete: () => void;
  onMove?: (direction: -1 | 1) => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
};

/** A food inside a meal — the core row plus its substitutions drawer.
 *
 *  Two ways in, because they answer different questions. The count badge in the name line is
 *  a READING ("this food already has two swaps") that happens to be clickable; the icon in
 *  the action cluster is the ACTION ("give this food a swap"), and it is the only one shown
 *  when there are none yet. */
const FoodRow = ({ item, onChange, onDelete, onMove, canMoveUp, canMoveDown }: Props) => {
  const [open, setOpen] = useState(false);
  const count = (item.substitutions || []).length;

  return (
    <Box>
      <FoodRowCore
        item={item}
        /* Changing this food changes the number its alternatives are meant to equal, so they are
           re-matched with it. Any the trainer pinned by typing a quantity are left alone. */
        onQuantityChange={(quantity) => onChange(withMatchedAlternatives({ ...item, quantity }))}
        onServingChange={(servingSizeId) =>
          onChange(withMatchedAlternatives(withServing(item, servingSizeId)))
        }
        onDelete={onDelete}
        onMove={onMove}
        canMoveUp={canMoveUp}
        canMoveDown={canMoveDown}
        badge={
          count > 0 && (
            <Box
              component="button"
              type="button"
              aria-label={`${open ? "הסתר" : "הצג"} חלופות ל${item.name}`}
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                padding: "2px 7px",
                borderRadius: "5px",
                border: "none",
                background: v2.lineSoft,
                color: v2.ink3,
                fontSize: "11px",
                fontWeight: 500,
                fontFamily: "inherit",
                cursor: "pointer",
                transition: "background .12s, color .12s",
                "&:hover": { background: "#E3E8ED", color: v2.ink },
              }}
            >
              {count === 1 ? "חלופה אחת" : `${count} חלופות`}
              <ExpandMore
                sx={{ fontSize: 13, transition: "transform .18s", transform: open ? "rotate(180deg)" : "none" }}
              />
            </Box>
          )
        }
        actions={
          <Tooltip title={count ? "ערוך חלופות" : "הוסף חלופה"} arrow placement="top">
            <Box
              component="button"
              type="button"
              aria-label={`הוסף חלופה ל${item.name}`}
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
              sx={{ ...rowIconBtn, color: open ? v2.brand : v2.ink3 }}
            >
              <AlternativesIcon sx={{ fontSize: 15 }} />
            </Box>
          </Tooltip>
        }
      />

      {open && (
        <SubstitutionsPanel
          item={item}
          onChange={(substitutions: SubGroupV2[]) => onChange({ ...item, substitutions })}
          /* Only this row can do the swap: the panel may rewrite `substitutions`, but promoting
             replaces the food the MEAL prescribes, and that item is ours. */
          onPromote={(groupUid, subUid) => onChange(promoteAlternative(item, groupUid, subUid))}
          /* the third way to close it, and the only one at the BOTTOM — both of the toggles
             above are off the top of the screen by the time a long list has been read */
          onClose={() => setOpen(false)}
        />
      )}
    </Box>
  );
};

export default FoodRow;
